import React, { ReactNode } from 'react';

import { Button, TitleWithDescription } from '@infoods/lifebase-ui';

import classes from './FoodNutrientsCard.module.scss';
import { Nutrient } from '../../../shared/components/ui-components';
import { NUTRIENTS_ENUM } from '../../../shared/constants/NUTRIENTS_ENUM';
import { getRoundedNumber } from '../../../shared/utils/mathUtils';
import { processMeasurement } from '../../../shared/utils/mealUtils';

/** values for the shown `amount`, not per unit - the card only rounds them */
export type FoodNutrientsCardFood = {
  name: string;
  amount: number;
  amountUnit: string | null;
  calorie: number; // NUTRIENTS_ENUM use calories, calorie is deprecated but used in food API
  proteins: number;
  carbs: number;
  fats: number;
  sugar: number;
  fiber: number;
  veggies: number;
  sodium: number;
};

type FoodNutrientsCardProps = {
  food: FoodNutrientsCardFood;
  actionIcon: ReactNode;
  onClick: () => void;
};

const FoodNutrientsCard = ({ food, actionIcon, onClick }: FoodNutrientsCardProps) => {
  const { name, amount, amountUnit } = food;

  const nutrients = [
    { nutrient: NUTRIENTS_ENUM.PROTEINS, label: 'Proteins', value: food.proteins },
    { nutrient: NUTRIENTS_ENUM.CARBS, label: 'Carbs', value: food.carbs },
    { nutrient: NUTRIENTS_ENUM.FATS, label: 'Fats', value: food.fats },
    { nutrient: NUTRIENTS_ENUM.SUGAR, label: 'Sugar', value: food.sugar },
    { nutrient: NUTRIENTS_ENUM.FIBER, label: 'Fiber', value: food.fiber },
    { nutrient: NUTRIENTS_ENUM.VEGGIES, label: 'Veggies', value: food.veggies },
    { nutrient: NUTRIENTS_ENUM.SODIUM, label: 'Sodium', value: food.sodium },
  ];

  const description = amountUnit ? `${amount} ${amountUnit}` : amount;

  return (
    <Button variant="unstyled" type="button" className={classes.card} onClick={onClick}>
      <div className={classes.foodInfoRow}>
        <TitleWithDescription
          titleFont="title-16-bold"
          title={name}
          description={
            <span className={classes.foodInfoDescription}>
              <span>{description}</span>
              <Nutrient nutrient={NUTRIENTS_ENUM.CALORIES}>
                {getRoundedNumber(food.calorie, 1)}
                &nbsp;
                {processMeasurement(NUTRIENTS_ENUM.CALORIES)}
              </Nutrient>
            </span>
          }
        />
        <span className={classes.actionIcon}>{actionIcon}</span>
      </div>
      <div className={classes.nutrientList}>
        {nutrients.map(({ nutrient, label, value }) => (
          <Nutrient key={nutrient} nutrient={nutrient}>
            <span className={classes.nutrient}>
              <span className={classes.nutrientLabel}>{label}</span>
              <span className={classes.nutrientValue}>{getRoundedNumber(value, 1)}</span>
            </span>
          </Nutrient>
        ))}
      </div>
    </Button>
  );
};

export { FoodNutrientsCard };
